import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { DataLabelWithTooltipProps } from './DataLabelWithTooltip.types'
import {
  DataLabel,
  DataLabelWrapper,
  HoverExplanation,
} from './DataLabelWithTooltip.styles'

export const DataLabelWithTooltip: React.FC<DataLabelWithTooltipProps> = ({
  label,
  explanation,
  children,
}) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number
    y: number
    placement: 'top' | 'bottom'
  }>({ x: 0, y: 0, placement: 'bottom' })
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const labelRef = useRef<HTMLDivElement>(null)

  const handleMouseEnter = () => {
    if (!explanation) return

    timeoutRef.current = setTimeout(() => {
      if (labelRef.current) {
        const rect = labelRef.current.getBoundingClientRect()
        const tooltipHeight = 120 // Approximate tooltip height
        const tooltipWidth = 280 // Tooltip width from styles
        const spaceBelow = window.innerHeight - rect.bottom
        const spaceAbove = rect.top

        let placement: 'top' | 'bottom' = 'bottom'
        let x = rect.left
        let y = rect.bottom + 8

        // Check vertical positioning
        if (spaceBelow < tooltipHeight && spaceAbove > spaceBelow) {
          placement = 'top'
          y = rect.top - tooltipHeight - 8
        }

        // Check horizontal positioning - keep tooltip within viewport
        if (x + tooltipWidth > window.innerWidth - 20) {
          x = window.innerWidth - tooltipWidth - 20
        }
        if (x < 20) {
          x = 20
        }

        setTooltipPosition({ x, y, placement })
      }
      setShowTooltip(true)
    }, 400)
  }

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setShowTooltip(false)
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  if (!explanation) {
    return <DataLabel>{children || label}</DataLabel>
  }

  return (
    <DataLabelWrapper
      ref={labelRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <DataLabel $hasTooltip={true}>{children || label}</DataLabel>
      {showTooltip &&
        createPortal(
          <HoverExplanation
            className="visible"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              opacity: 1,
              transform: 'scale(1)',
            }}
          >
            {explanation}
            {tooltipPosition.placement === 'top' && (
              <style>
                {`
                .visible::before {
                  top: auto !important;
                  bottom: -6px !important;
                  left: 20px !important;
                  transform: rotate(225deg) !important;
                }
              `}
              </style>
            )}
          </HoverExplanation>,
          document.body
        )}
    </DataLabelWrapper>
  )
}
